import {
  ref,
  onMounted,
  defineComponent,
  computed,
} from "@vue/composition-api";
import FormErrors from "@/components/FormErrors/FormErrors.vue";
import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";
import AutocompleteId from "@/components/Autocomplete/AutocompleteId.vue";
import { AutocompleteResult } from "@/components/Autocomplete/autocomplete-id";
import { v4 as uuidv4 } from "uuid";
import { JSConn } from "@/utilities/js-conn";

import { buildFormCommodityAutocomplete } from "@/models/Commodity";

import { buildSubmitButton, buildFormErrors } from "@/utilities/form-helpers";

import { NamedRts } from "@/router";
import { TOAST_OPTIONS } from "@/constants";

import { buildFormTemplateWorkOrder } from "@/models/TemplateWorkOrder";

import { TemplateWorkOrder } from "@/store/modules/templateWorkOrders";

export default defineComponent({
  name: "templateWorkOrderEditForm",
  components: {
    FormErrors,
    AutocompleteId,
    LoadingAnimation,
  },
  props: {
    templateWorkOrderId: {
      type: String,
      required: true,
    },
  },

  setup(props, ctx) {
    let $store = ctx.root.$store;

    const firstInput = ref<null | HTMLElement>(null);

    let dataLoaded = ref(false);
    let loadingMessage = ref("Preparing the form...");

    const deleting = ref(false);

    let deleteModal = ref<any>(null);

    const showDeleteModal = () => {
      deleteModal.value.show();
    };

    const hideDeleteModal = () => {
      deleteModal.value.hide();
    };

    let { updateCommodity } = buildFormCommodityAutocomplete();

    const localUpdateCommodity = (evt: AutocompleteResult) => {
      updateCommodity(
        evt,
        formTemplateWorkOrder.formCommodity,
        formTemplateWorkOrder,
      );
    };

    const commoditiesForAutocomplete = computed(() => {
      return $store.getters.commoditiesForAutocompleteId;
    });

    let { submitButtonText, submitting } = buildSubmitButton();
    let { hasFormErrors, formErrors } = buildFormErrors();

    const cancelParams = () => {
      if (props.templateWorkOrderId === "new") {
        return {
          name: NamedRts.templateWorkOrders,
        };
      } else {
        return {
          name: NamedRts.templateWorkOrders,
          params: {
            templatWorkOrderId: props.templateWorkOrderId,
          },
        };
      }
    };

    let {
      formTemplateWorkOrder,
      loadTemplateWorkOrderIntoForm,
    } = buildFormTemplateWorkOrder();

    const apiTemplateWorkOrder = computed(() => {
      return $store.getters.templateWorkOrder as TemplateWorkOrder;
    });

    onMounted(() => {
      const jsConnCommodities = new JSConn();
      const jsConn = new JSConn();

      jsConnCommodities.formParams = {};

      const dataPromises = [
        $store.dispatch("needCommodities", jsConnCommodities),
      ];

      if (props.templateWorkOrderId === "new") {
        Promise.all(dataPromises).then(() => {
          dataLoaded.value = true;
          ctx.root.$nextTick(() => {
            if (firstInput.value) {
              firstInput.value.focus();
            }
          });
          return Promise.resolve();
        });
      } else {
        jsConn.formParams = {
          id: props.templateWorkOrderId,
        };

        dataPromises.push($store.dispatch("needTemplateWorkOrder", jsConn));

        Promise.all(dataPromises).then(() => {
          // correct template work order is in the store

          loadTemplateWorkOrderIntoForm(
            apiTemplateWorkOrder.value,
            formTemplateWorkOrder,
          );

          dataLoaded.value = true;
          ctx.root.$nextTick(() => {
            if (firstInput.value) {
              firstInput.value.focus();
            }
          });
          return Promise.resolve();
        });
      }
    });

    const onSubmit = (evt: any) => {
      evt.preventDefault();
      const jsConn = new JSConn();

      hasFormErrors.value = false;
      formErrors.value = [];
      submitting.value = true;
      submitButtonText.value = "Submitting...";

      let storeDispatch;

      if (props.templateWorkOrderId === "new") {
        formTemplateWorkOrder.id = uuidv4();
        jsConn.formParams = { ...formTemplateWorkOrder };
        storeDispatch = "createTemplateWorkOrder";
      } else {
        jsConn.formParams = { ...formTemplateWorkOrder };
        storeDispatch = "updateTemplateWorkOrder";
      }

      $store
        .dispatch(storeDispatch, jsConn)
        .then(jsConn => {
          ctx.root.$bvToast.toast(
            "Template was successfully saved",
            TOAST_OPTIONS,
          );
          ctx.root.$router.push(cancelParams());
          return Promise.resolve(jsConn);
        })
        .catch(jsConn => {
          hasFormErrors.value = true;
          formErrors.value = jsConn.error["response"]["data"];
        })
        .finally(() => {
          submitting.value = false;
          submitButtonText.value = "Submit";
        });
    };

    const deleteTemplateWorkOrder = () => {
      const jsConn = new JSConn();
      jsConn.formParams = {
        id: props.templateWorkOrderId,
      };
      $store.dispatch("deleteTemplateWorkOrder", jsConn).then(jsConn => {
        ctx.root.$router.push(cancelParams());
        return Promise.resolve(jsConn);
      });
    };

    return {
      dataLoaded,
      loadingMessage,
      formTemplateWorkOrder,

      hasFormErrors,
      formErrors,
      cancelParams,
      firstInput,

      onSubmit,
      submitting,
      submitButtonText,

      deleting,
      deleteModal,
      showDeleteModal,
      hideDeleteModal,
      deleteTemplateWorkOrder,

      localUpdateCommodity,
      commoditiesForAutocomplete,
    };
  },
});
